import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import {
  allocationAttentionChip,
  allocationAttentionPhrase,
} from '../../../utils/allocationAttention';
import { AllocationNeedingConfirmationItem } from '../types';
import { PersonAvatar } from '../../shared/PersonAvatar';
import { widgetTokens } from '../designTokens';
import { DashboardWidget } from './DashboardWidget';
import { WidgetMoreLink } from './WidgetMoreLink';

interface AllocationsNeedingConfirmationWidgetProps {
  items?: AllocationNeedingConfirmationItem[];
  loading?: boolean;
  /** Opens the planning board with the Proposed filter already applied. */
  onNavigateToProposed?: () => void;
}

/**
 * "Needs confirming" — proposed allocations that have already started, or are
 * about to (AC-23). The dashboard half of the needs-confirming surface; the
 * board's `NeedsConfirmingBanner` is the other.
 *
 * Rides the existing `/api/dashboard/summary` payload, exactly like its sibling
 * `AllocationsExpiringWidget`, so the card costs no request (AC-25). Severity
 * and the signed day count come from the server, which applies the admin's
 * configured window; the widget only words them.
 */
export function AllocationsNeedingConfirmationWidget({
  items,
  loading,
  onNavigateToProposed,
}: AllocationsNeedingConfirmationWidgetProps) {
  const empty = !loading && (!items || items.length === 0);
  // Overdue first, then soonest — sorting on the item's own signed day count
  // rather than trusting the payload's order, so the card's stated ordering
  // holds whatever reaches it.
  const all = [...(items ?? [])].sort((a, b) => a.daysUntilStart - b.daysUntilStart);
  const visible = all.slice(0, widgetTokens.maxRows);
  const hidden = Math.max(0, all.length - visible.length);

  return (
    <DashboardWidget
      title="Needs confirming"
      icon={<PendingActionsIcon fontSize="small" />}
      iconBg="warning.50"
      accentColor="warning.main"
      cta={
        onNavigateToProposed
          ? { label: 'Review', onClick: onNavigateToProposed }
          : undefined
      }
      loading={loading}
      empty={empty}
      emptyMessage="Nothing needs confirming."
      data-testid="widget-allocations-needing-confirmation"
    >
      <List dense disablePadding>
        {visible.map((item) => {
          const chip = allocationAttentionChip(item.severity, item.daysUntilStart);
          return (
            <ListItem key={item.allocationId} disableGutters>
              <ListItemText
                primary={
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <PersonAvatar
                        name={item.employeeName}
                        src={item.avatarUrl}
                        size={22}
                        fontSize="0.625rem"
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500 }}
                        noWrap
                      >
                        {item.employeeName}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      color={chip.color}
                      variant="outlined"
                      label={chip.label}
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  </Stack>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {item.projectName} ·{' '}
                    {allocationAttentionPhrase(item.severity, item.daysUntilStart)}
                  </Typography>
                }
              />
            </ListItem>
          );
        })}
      </List>
      <WidgetMoreLink hiddenCount={hidden} onClick={onNavigateToProposed} />
    </DashboardWidget>
  );
}
